import { useTranslation } from 'react-i18next';

import { RelatedContentLink } from '@/components/content/related-content-link';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime } from '@/utils/format';

import type { ContentCommentRow, ContentModerationAction } from './content-comment';

const commentStatusVariant = (status: ContentCommentRow['status']): BadgeVariant => {
  if (status === 'published') return 'success';
  if (status === 'hidden') return 'warning';
  return 'danger';
};

export const ContentCommentDetailDialog = ({
  comment,
  open,
  onOpenChange,
  onModerate,
  onOpenRelated,
}: {
  comment: ContentCommentRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onModerate: (comment: ContentCommentRow, action: ContentModerationAction) => void;
  onOpenRelated: (comment: ContentCommentRow) => void;
}) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('comments.detail.title')}</DialogTitle>
        </DialogHeader>
        {comment ? (
          <>
            <DialogBody contentClassName="flex flex-col gap-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={commentStatusVariant(comment.status)}>
                  {t(`comments.status.${comment.status}`)}
                </Badge>
              </div>
              <p className="whitespace-pre-wrap text-body">{comment.body}</p>
              <dl className="grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-caption text-fg-secondary">{t('comments.columns.author')}</dt>
                  <dd className="text-body-sm">{comment.authorName}</dd>
                </div>
                <div>
                  <dt className="text-caption text-fg-secondary">{t('comments.columns.createdAt')}</dt>
                  <dd className="text-body-sm">{formatDateTime(comment.createdAt)}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-caption text-fg-secondary">{t('comments.columns.post')}</dt>
                  <dd className="mt-1">
                    <RelatedContentLink
                      contentType={comment.contentType}
                      contentTitle={comment.contentTitle}
                      onOpen={() => onOpenRelated(comment)}
                    />
                  </dd>
                </div>
              </dl>
            </DialogBody>
            <DialogFooter>
              {comment.status === 'published' ? (
                <>
                  <Button variant="outline" onClick={() => onModerate(comment, 'hide')}>
                    {t('comments.moderation.hide')}
                  </Button>
                  <Button variant="destructive" onClick={() => onModerate(comment, 'remove')}>
                    {t('comments.moderation.remove')}
                  </Button>
                </>
              ) : (
                <Button onClick={() => onModerate(comment, 'restore')}>
                  {t('comments.moderation.restore')}
                </Button>
              )}
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
